/* DentalChart — shared FDI permanent-dentition grid (32 teeth, 4 quadrants).
 *
 * iter85 (Wave B): used by both the per-tooth attachment picker and the
 * precision-cuts picker. Renders 4 rows × 8 cells; quadrant labels are
 * implicit (upper-right ←→ upper-left in the top half, lower-right ←→
 * lower-left in the bottom half, all reading left-to-right as seen
 * facing the patient — standard ortho convention).
 *
 * Props:
 *   teeth         — array of FDI strings (typically the metadata.fdi_teeth list)
 *   selectedTooth — string or null; the currently active tooth
 *   onSelect      — (tooth) => void; called when a cell is clicked
 *   marked        — Set<string> | object<tooth, anything>; cells with a value
 *                   render a colored dot to signal "has data"
 *   accent        — 'primary' | 'amber' | 'violet'; dot/highlight tint
 *   renderBadge   — optional (tooth) => ReactNode; shown inside the cell
 *                   (e.g. attachment purpose initial letter)
 */
import React from "react";

const ACCENT_RING = {
    primary: "ring-primary border-primary",
    amber: "ring-amber-500 border-amber-500",
    violet: "ring-violet-500 border-violet-500",
};
const ACCENT_DOT = {
    primary: "bg-primary",
    amber: "bg-amber-500",
    violet: "bg-violet-500",
};
const ACCENT_FILL = {
    primary: "bg-primary/10 text-primary border-primary/40",
    amber: "bg-amber-50 text-amber-900 border-amber-300",
    violet: "bg-violet-50 text-violet-900 border-violet-300",
};

export default function DentalChart({
    teeth,
    selectedTooth = null,
    onSelect,
    marked = new Set(),
    accent = "primary",
    renderBadge,
    testidPrefix = "dental-chart",
}) {
    // The metadata.fdi_teeth list is already ordered: rows of 8.
    // We chunk into 4 rows for layout.
    const rows = React.useMemo(() => {
        const out = [];
        for (let i = 0; i < teeth.length; i += 8) out.push(teeth.slice(i, i + 8));
        return out;
    }, [teeth]);
    const isMarked = (t) => {
        if (marked instanceof Set) return marked.has(t);
        return marked && Object.prototype.hasOwnProperty.call(marked, t);
    };
    return (
        <div
            className="rounded-xl border border-border bg-secondary/20 p-3 sm:p-4"
            data-testid={testidPrefix}
        >
            {/* Quadrant labels — quiet, only on >=sm so they don't crowd mobile */}
            <div className="hidden sm:flex justify-between text-[9px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">
                <span>Upper right (1.x)</span>
                <span>Upper left (2.x)</span>
            </div>
            <div className="space-y-1.5">
                {rows.map((row, ri) => (
                    <div key={ri} className="grid grid-cols-8 gap-1 sm:gap-1.5">
                        {/* Midline divider sits visually between cells 4 and 5
                            of each row — we just give cell 5 a small left
                            margin and a thin border on the left edge. */}
                        {row.map((tooth, ci) => {
                            const active = tooth === selectedTooth;
                            const has = isMarked(tooth);
                            const badge = renderBadge ? renderBadge(tooth) : null;
                            return (
                                <button
                                    key={tooth}
                                    type="button"
                                    onClick={() => onSelect?.(tooth)}
                                    className={[
                                        "relative h-10 sm:h-11 rounded-md border text-xs font-mono font-semibold transition-all",
                                        ci === 4 ? "ml-1 border-l-4 border-l-border/60" : "",
                                        active
                                            ? `${ACCENT_FILL[accent]} ring-2 ${ACCENT_RING[accent]}`
                                            : has
                                                ? `${ACCENT_FILL[accent]} border-transparent`
                                                : "bg-card text-muted-foreground hover:text-foreground hover:border-primary/40 border-border",
                                    ].join(" ")}
                                    data-testid={`${testidPrefix}-tooth-${tooth}`}
                                    aria-pressed={active}
                                    aria-label={`Tooth ${tooth}${has ? " (assigned)" : ""}`}
                                >
                                    <span className="block leading-none">{tooth}</span>
                                    {badge && (
                                        <span className="block text-[9px] leading-none mt-0.5 opacity-80">
                                            {badge}
                                        </span>
                                    )}
                                    {has && !active && (
                                        <span
                                            className={`absolute top-1 right-1 w-1.5 h-1.5 rounded-full ${ACCENT_DOT[accent]}`}
                                            aria-hidden
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                ))}
            </div>
            <div className="hidden sm:flex justify-between text-[9px] uppercase tracking-[0.18em] text-muted-foreground mt-1.5">
                <span>Lower right (4.x)</span>
                <span>Lower left (3.x)</span>
            </div>
        </div>
    );
}
